import { useCallback, useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { Bell, Bold, CreditCard, Download, EllipsisVertical, FileText, Italic, Paperclip, Phone, Plus, Search, Send, Smile, Tag, Underline, UserPlus, Video, X } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import { useMessageUnread } from '../hooks/useMessageUnread';
import InitialAvatar from '../components/InitialAvatar';

type PersonSummary = {
  id: string;
  name: string;
  title?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
};

type PatientTag = {
  id: string;
  name: string;
  mrn: string;
  status: string;
  avatarUrl?: string | null;
};

type MessageAttachment = {
  fileName: string;
  mimeType: string;
  sizeBytes?: number;
  url?: string;
  storageKey?: string;
};

type MessageEntityTag = {
  kind: 'PATIENT' | 'APPOINTMENT' | 'LAB_REPORT' | 'RADIOLOGY_STUDY' | 'RADIOLOGY_REPORT';
  refId: string;
  label: string;
  subtitle?: string;
  href?: string;
};

type ThreadMessage = {
  id: string;
  sender: PersonSummary | null;
  message: string;
  messageHtml?: string | null;
  attachments?: MessageAttachment[];
  entityTags?: MessageEntityTag[];
  createdAt: string;
};

type FolderSummary = {
  id: string;
  name: string;
  systemKey: string | null;
  color?: string | null;
  threadCount: number;
  unreadCount: number;
};

type ThreadListItem = {
  id: string;
  subject: string | null;
  lastMessageAt: string | null;
  updatedAt: string;
  participants: PersonSummary[];
  latestMessage: ThreadMessage | null;
  patientTags: PatientTag[];
  unreadCount: number | null;
  folder: { id: string; name: string; systemKey: string | null; color?: string | null } | null;
};

type ThreadDetail = {
  id: string;
  subject: string | null;
  participants: PersonSummary[];
  patientTags: PatientTag[];
  messages: ThreadMessage[];
  folder: { id: string; name: string; systemKey: string | null; color?: string | null } | null;
  messagesMeta: { total: number; hasMore: boolean };
};

type LookupRecipient = PersonSummary & { role: string };
type LookupPatient = PatientTag;
type LookupReference = MessageEntityTag;

type FoldersResponse = {
  data: FolderSummary[];
  meta: { defaultFolderId: string };
};

function translateFolderName(
  folder: Pick<FolderSummary, 'name' | 'systemKey'> | Pick<ThreadListItem['folder'], 'name' | 'systemKey'>,
  tr: (value: string) => string,
) {
  const systemKey = String(folder.systemKey || '').toUpperCase();
  if (systemKey === 'INBOX') return tr('Inbox');
  if (systemKey === 'ARCHIVE') return tr('Archive');
  if (systemKey === 'WAITING_ON_PATIENT') return tr('Waiting on Patient');

  const normalized = String(folder.name || '').trim();
  if (normalized === 'Inbox') return tr('Inbox');
  if (normalized === 'Archive') return tr('Archive');
  if (normalized === 'Waiting on Patient') return tr('Waiting on Patient');
  return normalized;
}

const COMPOSER_EMOJIS = [
  '🙂', '😀', '😄', '😁', '😉', '😊', '😍', '🥰',
  '😘', '😎', '🤗', '🤔', '😴', '🤒', '🤕', '😷',
  '🙏', '💙', '❤️', '👍', '👏', '🎉', '✨', '🔥',
];

function Avatar({
  name,
  avatarUrl,
  size = 28,
}: {
  name: string;
  avatarUrl?: string | null;
  size?: number;
}) {
  return <InitialAvatar name={name} src={avatarUrl} alt={name} style={{ width: size, height: size, minWidth: size }} />;
}

function htmlToPlainText(html: string) {
  const root = document.createElement('div');
  root.innerHTML = html || '';
  return (root.textContent || root.innerText || '').trim();
}

function sanitizeMessageHtml(value: string) {
  const source = String(value || '').trim();
  if (!source) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(source, 'text/html');
  const allowed = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'A', 'SPAN', 'CODE', 'PRE', 'BLOCKQUOTE']);
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
  const toReplace: Element[] = [];
  while (walker.nextNode()) {
    const node = walker.currentNode as Element;
    if (!allowed.has(node.tagName)) {
      toReplace.push(node);
      continue;
    }
    for (const attr of Array.from(node.attributes)) {
      if (node.tagName === 'A' && ['href', 'target', 'rel'].includes(attr.name.toLowerCase())) continue;
      node.removeAttribute(attr.name);
    }
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (!/^https?:\/\//i.test(href) && !href.startsWith('/')) {
        node.removeAttribute('href');
      }
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
  toReplace.forEach((node) => node.replaceWith(...Array.from(node.childNodes)));
  return doc.body.innerHTML.trim();
}

function RichEditor({
  editorRef,
  value,
  onChange,
  placeholder,
}: {
  editorRef: RefObject<HTMLDivElement | null>;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const normalized = value || '<p></p>';
    if (el.innerHTML !== normalized) {
      el.innerHTML = normalized;
    }
  }, [editorRef, value]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center gap-1 border-b border-slate-100 p-1.5">
        <button type="button" className="rounded p-1 hover:bg-slate-100" onClick={() => document.execCommand('bold')}>
          <Bold size={14} />
        </button>
        <button type="button" className="rounded p-1 hover:bg-slate-100" onClick={() => document.execCommand('italic')}>
          <Italic size={14} />
        </button>
        <button type="button" className="rounded p-1 hover:bg-slate-100" onClick={() => document.execCommand('underline')}>
          <Underline size={14} />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="min-h-[90px] px-3 py-2 text-sm outline-none"
        data-placeholder={placeholder}
        onInput={(event) => {
          const raw = (event.currentTarget as HTMLDivElement).innerHTML || '';
          const sanitized = sanitizeMessageHtml(raw);
          if (sanitized !== raw) {
            (event.currentTarget as HTMLDivElement).innerHTML = sanitized;
          }
          onChange(sanitized);
        }}
      />
    </div>
  );
}

export default function Messages() {
  const { fetchJson } = useApiClient();
  const { tr } = useI18n();
  const { refresh: refreshUnread } = useMessageUnread(30000);

  const [folders, setFolders] = useState<FolderSummary[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [threads, setThreads] = useState<ThreadListItem[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [activeThread, setActiveThread] = useState<ThreadDetail | null>(null);

  const [loadingFolders, setLoadingFolders] = useState(true);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [composer, setComposer] = useState('');
  const [composerHtml, setComposerHtml] = useState('<p></p>');
  const composerEditorRef = useRef<HTMLTextAreaElement | null>(null);
  const replyAttachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [composerAttachments, setComposerAttachments] = useState<MessageAttachment[]>([]);
  const [composerEntityTags, setComposerEntityTags] = useState<LookupReference[]>([]);
  const [composerReferenceKind, setComposerReferenceKind] = useState<LookupReference['kind']>('APPOINTMENT');
  const [composerReferenceQuery, setComposerReferenceQuery] = useState('');
  const [composerReferenceOptions, setComposerReferenceOptions] = useState<LookupReference[]>([]);
  const [showComposerEmojiTray, setShowComposerEmojiTray] = useState(false);
  const [uploadingComposerAttachment, setUploadingComposerAttachment] = useState(false);
  const [sending, setSending] = useState(false);
  const [movingThread, setMovingThread] = useState(false);
  const [draggedThreadId, setDraggedThreadId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);

  const [showCompose, setShowCompose] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newMessageHtml, setNewMessageHtml] = useState('<p></p>');
  const newThreadEditorRef = useRef<HTMLDivElement | null>(null);
  const newThreadAttachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [newThreadAttachments, setNewThreadAttachments] = useState<MessageAttachment[]>([]);
  const [newThreadEntityTags, setNewThreadEntityTags] = useState<LookupReference[]>([]);
  const [newThreadReferenceKind, setNewThreadReferenceKind] = useState<LookupReference['kind']>('APPOINTMENT');
  const [newThreadReferenceQuery, setNewThreadReferenceQuery] = useState('');
  const [newThreadReferenceOptions, setNewThreadReferenceOptions] = useState<LookupReference[]>([]);
  const [uploadingNewThreadAttachment, setUploadingNewThreadAttachment] = useState(false);
  const [creatingThread, setCreatingThread] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<LookupRecipient[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<LookupPatient[]>([]);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientOptions, setRecipientOptions] = useState<LookupRecipient[]>([]);
  const [patientQuery, setPatientQuery] = useState('');
  const [patientOptions, setPatientOptions] = useState<LookupPatient[]>([]);

  const [threadPatientQuery, setThreadPatientQuery] = useState('');
  const [threadPatientOptions, setThreadPatientOptions] = useState<LookupPatient[]>([]);
  const [taggingPatient, setTaggingPatient] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [clientNotificationsEnabled, setClientNotificationsEnabled] = useState(true);
  const [autoTopUpEnabled, setAutoTopUpEnabled] = useState(true);
  const [notificationTemplate, setNotificationTemplate] = useState('Hi {{client.firstName}}, your {{appointment.type}} appointment is {{appointment.date}}. {{cancellation_policy}}');

  const loadFolders = useCallback(async () => {
    setLoadingFolders(true);
    try {
      const payload = await fetchJson<FoldersResponse>('/messages/folders');
      const nextFolders = payload.data ?? [];
      setFolders(nextFolders);
      if (!selectedFolderId) {
        setSelectedFolderId(payload.meta?.defaultFolderId || nextFolders[0]?.id || null);
      } else if (!nextFolders.some((folder) => folder.id === selectedFolderId)) {
        setSelectedFolderId(payload.meta?.defaultFolderId || nextFolders[0]?.id || null);
      }
    } catch (err: any) {
      setError(err?.message || tr('Failed to load folders.'));
    } finally {
      setLoadingFolders(false);
    }
  }, [fetchJson, selectedFolderId, tr]);

  const loadThreads = useCallback(async () => {
    if (!selectedFolderId) return;
    setLoadingThreads(true);
    try {
      const payload = await fetchJson<{ data: ThreadListItem[] }>(
        `/messages/threads?limit=80&folderId=${encodeURIComponent(selectedFolderId)}`,
      );
      const nextThreads = payload.data ?? [];
      setThreads(nextThreads);
      if (!activeThreadId && nextThreads[0]) {
        setActiveThreadId(nextThreads[0].id);
      } else if (activeThreadId && !nextThreads.some((thread) => thread.id === activeThreadId)) {
        setActiveThreadId(nextThreads[0]?.id ?? null);
      }
    } catch (err: any) {
      setError(err?.message || tr('Failed to load threads.'));
    } finally {
      setLoadingThreads(false);
    }
  }, [activeThreadId, fetchJson, selectedFolderId, tr]);

  const loadThread = useCallback(
    async (threadId: string) => {
      setLoadingThread(true);
      try {
        const payload = await fetchJson<{ data: ThreadDetail }>(`/messages/threads/${threadId}?limit=200`);
        setActiveThread(payload.data);
        setActiveThreadId(threadId);
        const latestMessageId = payload.data.messages[payload.data.messages.length - 1]?.id;
        await fetchJson(`/messages/threads/${threadId}/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lastReadMessageId: latestMessageId }),
        });
        await refreshUnread();
      } catch (err: any) {
        setError(err?.message || tr('Failed to load conversation.'));
      } finally {
        setLoadingThread(false);
      }
    },
    [fetchJson, refreshUnread, tr],
  );

  const loadRecipientOptions = useCallback(
    async (query: string) => {
      const payload = await fetchJson<{ data: LookupRecipient[] }>(
        `/messages/recipients?limit=12&q=${encodeURIComponent(query || 'a')}`,
      );
      setRecipientOptions(payload.data ?? []);
    },
    [fetchJson],
  );

  const loadPatientOptions = useCallback(
    async (query: string, setter: (rows: LookupPatient[]) => void) => {
      const payload = await fetchJson<{ data: LookupPatient[] }>(
        `/messages/patients/lookup?limit=12&q=${encodeURIComponent(query || 'a')}`,
      );
      setter(payload.data ?? []);
    },
    [fetchJson],
  );

  const loadReferenceOptions = useCallback(
    async (
      kind: LookupReference['kind'],
      query: string,
      setter: (rows: LookupReference[]) => void,
    ) => {
      const payload = await fetchJson<{ data: LookupReference[] }>(
        `/messages/references/lookup?kind=${encodeURIComponent(kind)}&limit=12&q=${encodeURIComponent(query || 'a')}`,
      );
      setter(payload.data ?? []);
    },
    [fetchJson],
  );

  const uploadMessageAttachments = useCallback(
    async (
      files: File[],
      setRows: Dispatch<SetStateAction<MessageAttachment[]>>,
      setUploading: Dispatch<SetStateAction<boolean>>,
    ) => {
      if (!files.length) return;
      setUploading(true);
      try {
        for (const file of files) {
          const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>(`/storage/presign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
              kind: 'message-attachment',
            }),
          });
          const uploadRes = await fetch(presign.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
            },
          });
          if (!uploadRes.ok) {
            throw new Error(`Upload failed (${uploadRes.status})`);
          }
          setRows((current) => [
            ...current,
            {
              fileName: file.name,
              mimeType: file.type || 'application/octet-stream',
              sizeBytes: file.size,
              url: presign.url,
              storageKey: presign.key,
            },
          ]);
        }
      } catch (err: any) {
        setError(err?.message || tr('Attachment upload failed.'));
      } finally {
        setUploading(false);
      }
    },
    [fetchJson, tr],
  );

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    if (selectedFolderId) {
      void loadThreads();
    }
  }, [loadThreads, selectedFolderId]);

  useEffect(() => {
    if (activeThreadId) {
      void loadThread(activeThreadId);
    } else {
      setActiveThread(null);
    }
  }, [activeThreadId, loadThread]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadRecipientOptions(recipientQuery);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [loadRecipientOptions, recipientQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadPatientOptions(patientQuery, setPatientOptions);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [loadPatientOptions, patientQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadPatientOptions(threadPatientQuery, setThreadPatientOptions);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [loadPatientOptions, threadPatientQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadReferenceOptions(composerReferenceKind, composerReferenceQuery, setComposerReferenceOptions);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [composerReferenceKind, composerReferenceQuery, loadReferenceOptions]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadReferenceOptions(newThreadReferenceKind, newThreadReferenceQuery, setNewThreadReferenceOptions);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [loadReferenceOptions, newThreadReferenceKind, newThreadReferenceQuery]);

  useEffect(() => {
    setShowComposerEmojiTray(false);
  }, [activeThreadId]);

  const sendMessage = async () => {
    const textMessage = composer.trim();
    const hasAttachments = composerAttachments.length > 0;
    if (!activeThreadId || (!textMessage && !hasAttachments)) return;
    setSending(true);
    try {
      await fetchJson(`/messages/threads/${activeThreadId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: textMessage || tr('Attachment'),
          messageHtml: undefined,
          attachments: composerAttachments.length ? composerAttachments : undefined,
          entityTags: composerEntityTags.length ? composerEntityTags : undefined,
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      setComposer('');
      setComposerHtml('<p></p>');
      setShowComposerEmojiTray(false);
      setComposerAttachments([]);
      setComposerEntityTags([]);
      await Promise.all([loadThread(activeThreadId), loadThreads(), loadFolders(), refreshUnread()]);
    } catch (err: any) {
      setError(err?.message || tr('Failed to send message.'));
    } finally {
      setSending(false);
    }
  };

  const appendComposerEmoji = (emoji: string) => {
    setComposer((current) => `${current}${current ? ' ' : ''}${emoji}`);
    setShowComposerEmojiTray(false);
  };

  const downloadAttachment = useCallback(
    async (item: MessageAttachment) => {
      if (!item.url) {
        setError(tr('Attachment is unavailable.'));
        return;
      }
      try {
        const response = await fetch(item.url);
        if (!response.ok) throw new Error(`Download failed (${response.status})`);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = item.fileName || 'attachment';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch {
        window.open(item.url, '_blank', 'noopener,noreferrer');
      }
    },
    [tr],
  );

  const createThread = async () => {
    const normalizedHtml = sanitizeMessageHtml(newMessageHtml);
    const textMessage = newMessage.trim() || htmlToPlainText(normalizedHtml);
    if (!textMessage || selectedRecipients.length === 0) return;
    setCreatingThread(true);
    try {
      const payload = await fetchJson<{ data: { id: string } }>('/messages/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: newSubject.trim() || undefined,
          message: textMessage,
          messageHtml: normalizedHtml || undefined,
          participantIds: selectedRecipients.map((recipient) => recipient.id),
          patientIds: selectedPatients.map((patient) => patient.id),
          attachments: newThreadAttachments.length ? newThreadAttachments : undefined,
          entityTags: newThreadEntityTags.length ? newThreadEntityTags : undefined,
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      setNewSubject('');
      setNewMessage('');
      setNewMessageHtml('<p></p>');
      setNewThreadAttachments([]);
      setNewThreadEntityTags([]);
      setSelectedRecipients([]);
      setSelectedPatients([]);
      closeCompose();
      await Promise.all([loadFolders(), loadThreads(), refreshUnread()]);
      if (payload.data?.id) {
        setActiveThreadId(payload.data.id);
      }
    } catch (err: any) {
      setError(err?.message || tr('Failed to create thread.'));
    } finally {
      setCreatingThread(false);
    }
  };

  const closeCompose = () => {
    setShowCompose(false);
    setNewSubject('');
    setNewMessage('');
    setNewMessageHtml('<p></p>');
    setSelectedRecipients([]);
    setSelectedPatients([]);
    setNewThreadAttachments([]);
    setNewThreadEntityTags([]);
    setRecipientQuery('');
    setPatientQuery('');
    setNewThreadReferenceQuery('');
  };

  const moveThread = async (threadId: string, folderId: string) => {
    if (!threadId || !folderId || movingThread) return;
    setMovingThread(true);
    try {
      await fetchJson(`/messages/threads/${threadId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId }),
      });
      setDragOverFolderId(null);
      setDraggedThreadId(null);
      await Promise.all([
        loadFolders(),
        loadThreads(),
        activeThreadId === threadId ? loadThread(threadId) : Promise.resolve(),
      ]);
      if (folderId !== selectedFolderId) {
        setSelectedFolderId(folderId);
      }
    } catch (err: any) {
      setError(err?.message || tr('Failed to move thread.'));
    } finally {
      setMovingThread(false);
    }
  };

  const addPatientTagToActiveThread = async (patientId: string) => {
    if (!activeThreadId || !patientId || taggingPatient) return;
    setTaggingPatient(true);
    try {
      await fetchJson(`/messages/threads/${activeThreadId}/patient-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientIds: [patientId] }),
      });
      setThreadPatientQuery('');
      await Promise.all([loadThread(activeThreadId), loadThreads()]);
    } catch (err: any) {
      setError(err?.message || tr('Failed to add patient tag.'));
    } finally {
      setTaggingPatient(false);
    }
  };

  const removePatientTag = async (patientId: string) => {
    if (!activeThreadId) return;
    try {
      await fetchJson(`/messages/threads/${activeThreadId}/patient-tags/${patientId}`, {
        method: 'DELETE',
      });
      await Promise.all([loadThread(activeThreadId), loadThreads()]);
    } catch (err: any) {
      setError(err?.message || tr('Failed to remove patient tag.'));
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim() || creatingFolder) return;
    setCreatingFolder(true);
    try {
      await fetchJson('/messages/folders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFolderName.trim() }),
      });
      setNewFolderName('');
      setShowCreateFolderDialog(false);
      await loadFolders();
    } catch (err: any) {
      setError(err?.message || tr('Failed to create folder.'));
    } finally {
      setCreatingFolder(false);
    }
  };

  const selectableRecipients = recipientOptions.filter(
    (candidate) => !selectedRecipients.some((selected) => selected.id === candidate.id),
  );
  const selectablePatients = patientOptions.filter(
    (candidate) => !selectedPatients.some((selected) => selected.id === candidate.id),
  );
  const selectableThreadPatients = threadPatientOptions.filter(
    (candidate) => !activeThread?.patientTags.some((tag) => tag.id === candidate.id),
  );
  const selectableComposerRefs = composerReferenceOptions.filter(
    (candidate) => !composerEntityTags.some((selected) => selected.kind === candidate.kind && selected.refId === candidate.refId),
  );
  const selectableNewThreadRefs = newThreadReferenceOptions.filter(
    (candidate) => !newThreadEntityTags.some((selected) => selected.kind === candidate.kind && selected.refId === candidate.refId),
  );
  const activeRecipient = activeThread?.participants[0] || null;

  return (
    <div className="relative h-full w-full bg-white overflow-hidden">
      <div className="grid h-full lg:grid-cols-[260px_360px_minmax(0,1fr)]">
        <aside className="messages-scroll border-r border-slate-100 p-4 overflow-y-auto">
          <button
            type="button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 text-sm font-semibold text-white"
            onClick={() => setShowCompose((prev) => !prev)}
          >
            <Plus size={16} />
            {showCompose ? tr('Close compose') : tr('Compose')}
          </button>
          <div className="mt-4 space-y-2">
            {loadingFolders ? <p className="text-xs text-slate-400">{tr('Loading folders...')}</p> : null}
            {folders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                  dragOverFolderId === folder.id
                    ? 'bg-blue-50 text-blue-700 ring-2 ring-blue-200'
                    : selectedFolderId === folder.id
                      ? 'bg-ink text-white'
                      : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => setSelectedFolderId(folder.id)}
                onDragOver={(event) => {
                  if (!draggedThreadId || folder.id === selectedFolderId) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                  setDragOverFolderId(folder.id);
                }}
                onDragLeave={() => {
                  if (dragOverFolderId === folder.id) {
                    setDragOverFolderId(null);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  const threadId = event.dataTransfer.getData('text/plain') || draggedThreadId;
                  if (!threadId || folder.id === selectedFolderId) {
                    setDragOverFolderId(null);
                    setDraggedThreadId(null);
                    return;
                  }
                  void moveThread(threadId, folder.id);
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">{translateFolderName(folder, tr)}</span>
                  {folder.unreadCount > 0 ? (
                    <span className={`inline-flex min-w-[20px] h-5 items-center justify-center rounded-full px-1 text-xs ${selectedFolderId === folder.id ? 'bg-white text-ink' : 'bg-ink text-white'}`}>
                      {folder.unreadCount}
                    </span>
                  ) : null}
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Folders')}</p>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
                onClick={() => setShowCreateFolderDialog(true)}
                aria-label={tr('Create folder')}
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <div className="mt-6 space-y-3 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-ink" />
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Notifications')}</p>
            </div>
            <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
              <span>{tr('Client notifications')}</span>
              <input type="checkbox" checked={clientNotificationsEnabled} onChange={(event) => setClientNotificationsEnabled(event.target.checked)} className="accent-ink" />
            </label>
            <div className="space-y-2 rounded-lg border border-slate-100 bg-white p-3">
              <p className="text-xs font-semibold text-slate-700">{tr('Template library')}</p>
              {['Booked', 'Reminder', 'Reschedule', 'Feedback', 'Cancel', 'No-show', 'Waitlist', 'Quote'].map((item) => (
                <button
                  key={item}
                  type="button"
                  className="block w-full rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-100"
                  onClick={() => setNotificationTemplate(`${item}: Hi {{client.firstName}}, update for {{appointment.date}}. {{merge_tag}}`)}
                >
                  {item}
                </button>
              ))}
            </div>
            <textarea
              className="min-h-24 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"
              value={notificationTemplate}
              onChange={(event) => setNotificationTemplate(event.target.value)}
            />
            <div className="flex flex-wrap gap-1">
              {['{{client.firstName}}', '{{appointment.date}}', '{{cancellation_policy}}', '{{virtual_link}}'].map((tag) => (
                <button key={tag} type="button" className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px]" onClick={() => setNotificationTemplate((current) => `${current} ${tag}`)}>
                  {tag}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
              {['Letters', 'Pre-care', 'Aftercare', 'Attachments', 'Virtual reminders', 'Quote follow-up'].map((item) => (
                <div key={item} className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5">{tr(item)}</div>
              ))}
            </div>
            <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
              <span className="inline-flex items-center gap-1"><CreditCard size={13} /> {tr('Auto top-up credits')}</span>
              <input type="checkbox" checked={autoTopUpEnabled} onChange={(event) => setAutoTopUpEnabled(event.target.checked)} className="accent-ink" />
            </label>
          </div>

        </aside>

        <section className="messages-scroll border-r border-slate-100 overflow-y-auto">
          <div className="border-b border-slate-100 p-3">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <input className="w-full rounded-lg border border-slate-200 px-3 py-2 pl-7 text-sm" placeholder={tr('Search in folder')} />
            </div>
          </div>
          {loadingThreads ? <p className="p-4 text-xs text-slate-400">{tr('Loading threads...')}</p> : null}
          {!loadingThreads && threads.length === 0 ? <p className="p-4 text-sm text-slate-500">{tr('No conversations in this folder.')}</p> : null}
          <div className="divide-y divide-slate-100">
            {threads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => setActiveThreadId(thread.id)}
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData('text/plain', thread.id);
                  event.dataTransfer.effectAllowed = 'move';
                  setDraggedThreadId(thread.id);
                }}
                onDragEnd={() => {
                  setDraggedThreadId(null);
                  setDragOverFolderId(null);
                }}
                className={`w-full p-3 text-left transition ${thread.id === activeThreadId ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
              >
                <div className="flex items-start gap-3">
                  <Avatar name={thread.participants[0]?.name || 'User'} avatarUrl={thread.participants[0]?.avatarUrl} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-semibold text-ink">{thread.subject || tr('Untitled conversation')}</p>
                      {thread.unreadCount ? <span className="text-xs font-semibold text-ink">{thread.unreadCount}</span> : null}
                    </div>
                    <p className="truncate text-xs text-slate-500">{thread.participants.map((person) => person.name).join(', ')}</p>
                    <p className="mt-1 truncate text-xs text-slate-500">
                      {thread.latestMessage?.message || htmlToPlainText(thread.latestMessage?.messageHtml || '') || tr('No messages yet')}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="flex min-h-0 flex-col">
          <header className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative">
                <Avatar name={activeRecipient?.name || tr('Conversation')} avatarUrl={activeRecipient?.avatarUrl} size={54} />
                <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" />
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-2xl font-semibold tracking-tight text-ink">
                  {activeRecipient?.name || activeThread?.subject || tr('Select a conversation')}
                </h2>
                <p className="truncate text-sm text-slate-500">
                  {activeRecipient?.title || activeRecipient?.email || tr('Conversation participant')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {[Phone, Video, EllipsisVertical].map((Icon, index) => (
                <button
                  key={index}
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                >
                  <Icon size={17} />
                </button>
              ))}
            </div>
          </header>

          <div className="messages-scroll flex-1 overflow-y-auto bg-white px-6 py-4 space-y-3.5">
            {loadingThread ? <p className="text-sm text-slate-400">{tr('Loading conversation...')}</p> : null}
            {!loadingThread && !activeThread ? <p className="text-sm text-slate-500">{tr('Select a thread to view messages.')}</p> : null}
            {activeThread?.messages.map((message, index) => {
              const recipientId = activeRecipient?.id;
              const recipientName = activeRecipient?.name?.toLowerCase() || '';
              const senderName = message.sender?.name?.toLowerCase() || '';
              const mine = recipientId
                ? message.sender?.id !== recipientId
                : senderName
                  ? senderName !== recipientName
                  : index % 2 === 1;
              const previous = activeThread.messages[index - 1];
              const next = activeThread.messages[index + 1];
              const prevMine = previous
                ? recipientId
                  ? previous.sender?.id !== recipientId
                  : previous.sender?.name?.toLowerCase() !== recipientName
                : null;
              const nextMine = next
                ? recipientId
                  ? next.sender?.id !== recipientId
                  : next.sender?.name?.toLowerCase() !== recipientName
                : null;
              const showLabel = prevMine === null || prevMine !== mine;
              const showSeen = mine && (nextMine === null || nextMine !== mine);

              return (
                <article key={message.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[86%] md:max-w-[680px]">
                    {showLabel ? (
                      <p className={`mb-1.5 text-[13px] font-semibold text-slate-900 ${mine ? 'text-right' : ''}`}>
                        {mine ? <span className="text-[11px] font-medium text-slate-500">{new Date(message.createdAt).toLocaleString()} </span> : null}
                        {message.sender?.name || tr('System')}
                        {!mine ? <span className="text-[11px] font-medium text-slate-500"> {new Date(message.createdAt).toLocaleString()}</span> : null}
                      </p>
                    ) : null}
                    <div className={`inline-block w-fit max-w-full rounded-xl border px-3 py-2.5 ${mine ? 'border-blue-100 bg-blue-50/70' : 'border-slate-100 bg-slate-50'}`}>
                      {message.message?.trim() ? (
                        <p className="whitespace-pre-wrap text-[13px] leading-5 text-slate-700">{message.message}</p>
                      ) : message.messageHtml && htmlToPlainText(message.messageHtml).trim() ? (
                        <div
                          className="prose prose-sm max-w-full text-[13px] leading-relaxed text-slate-700"
                          dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(message.messageHtml) }}
                        />
                      ) : (
                        <p className="text-[12px] italic text-slate-400">{tr('Attachment')}</p>
                      )}
                      {message.entityTags?.length ? (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {message.entityTags.map((tag) => (
                            <a
                              key={`${message.id}-${tag.kind}-${tag.refId}`}
                              className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-700 hover:bg-sky-100"
                              href={tag.href || '#'}
                              onClick={(event) => {
                                if (!tag.href) event.preventDefault();
                              }}
                            >
                              {tag.label}
                            </a>
                          ))}
                        </div>
                      ) : null}
                      {message.attachments?.length ? (
                        <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
                          {message.attachments.map((item, attachmentIndex) => (
                            <button
                              key={`${message.id}-attachment-${attachmentIndex}`}
                              type="button"
                              onClick={() => void downloadAttachment(item)}
                              className="inline-flex max-w-[190px] items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1 text-left transition hover:border-slate-300"
                            >
                              <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-slate-100 text-slate-600">
                                <FileText size={13} />
                              </span>
                              <span className="truncate text-[10px] text-slate-600">
                                {item.fileName}
                              </span>
                              <Download size={12} className="shrink-0 text-slate-400" />
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    {!mine ? (
                      <div className="mt-1">
                        <Avatar name={message.sender?.name || 'System'} avatarUrl={message.sender?.avatarUrl} size={22} />
                      </div>
                    ) : null}
                    {showSeen ? <p className="mt-1 text-right text-xs font-semibold text-blue-600">{tr('Seen')}</p> : null}
                  </div>
                </article>
              );
            })}
          </div>

          <footer className="border-t border-slate-200 bg-white p-4">
            <div className="rounded-2xl border border-blue-300 px-4 py-3 shadow-sm">
              <textarea
                ref={composerEditorRef}
                value={composer}
                onChange={(event) => setComposer(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void sendMessage();
                  }
                }}
                placeholder={tr('Write a message')}
                className="min-h-[70px] w-full resize-none border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
              />
              {(composerEntityTags.length || composerAttachments.length) ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {composerEntityTags.map((tag) => (
                    <span key={`${tag.kind}-${tag.refId}`} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] text-sky-700">
                      {tag.label}
                      <button type="button" onClick={() => setComposerEntityTags((current) => current.filter((item) => !(item.kind === tag.kind && item.refId === tag.refId)))}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {composerAttachments.map((file, idx) => (
                    <span key={`${file.fileName}-${idx}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">
                      {file.fileName}
                      <button type="button" onClick={() => setComposerAttachments((current) => current.filter((_, i) => i !== idx))}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
              <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2">
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => setComposer((current) => `${current}${current ? ' ' : ''}@`)}
                    aria-label={tr('Tag')}
                  >
                    <Tag size={16} />
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                      onClick={() => setShowComposerEmojiTray((current) => !current)}
                      aria-label={tr('Emoji')}
                    >
                      <Smile size={16} />
                    </button>
                    {showComposerEmojiTray ? (
                      <div className="absolute bottom-11 left-0 z-20 w-64 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                        <div className="grid grid-cols-8 gap-1">
                          {COMPOSER_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-base hover:bg-slate-100"
                              onClick={() => appendComposerEmoji(emoji)}
                              aria-label={`${tr('Emoji')} ${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => replyAttachmentInputRef.current?.click()}
                    disabled={!activeThreadId || uploadingComposerAttachment}
                    aria-label={tr('Attach')}
                  >
                    <Paperclip size={16} />
                  </button>
                  <input
                    ref={replyAttachmentInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(event) => {
                      const files = Array.from(event.target.files || []);
                      if (files.length) void uploadMessageAttachments(files, setComposerAttachments, setUploadingComposerAttachment);
                      event.currentTarget.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                    aria-label={tr('More')}
                  >
                    <EllipsisVertical size={16} />
                  </button>
                </div>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-lg bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-50"
                  onClick={() => void sendMessage()}
                  disabled={!activeThreadId || (!composer.trim() && !composerAttachments.length) || sending}
                >
                  <Send size={14} />
                  {sending ? tr('Sending...') : tr('Send')}
                </button>
              </div>
            </div>
          </footer>
        </section>
      </div>

      {showCompose ? (
        <div className="absolute bottom-0 right-4 z-30 w-[460px] max-w-[calc(100%-1rem)] rounded-t-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between rounded-t-2xl bg-slate-100 px-4 py-3">
            <p className="text-sm font-semibold text-ink">{tr('New message')}</p>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200"
              onClick={closeCompose}
              aria-label={tr('Close compose')}
            >
              <X size={14} />
            </button>
          </div>
          <div className="space-y-3 p-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Recipients')}</p>
              <div className="flex flex-wrap gap-2">
                {selectedRecipients.map((recipient) => (
                  <span key={recipient.id} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2 py-1 text-xs">
                    <Avatar name={recipient.name} avatarUrl={recipient.avatarUrl} size={20} />
                    {recipient.name}
                    <button type="button" onClick={() => setSelectedRecipients((current) => current.filter((item) => item.id !== recipient.id))}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="relative">
                <UserPlus size={14} className="absolute left-2 top-2.5 text-slate-400" />
                <input
                  className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm"
                  value={recipientQuery}
                  onChange={(event) => setRecipientQuery(event.target.value)}
                  placeholder={tr('Search recipients')}
                />
              </div>
              {recipientQuery && selectableRecipients.length > 0 ? (
                <div className="max-h-28 overflow-auto rounded-lg border border-slate-200 bg-white">
                  {selectableRecipients.map((recipient) => (
                    <button
                      key={recipient.id}
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        setSelectedRecipients((current) => [...current, recipient]);
                        setRecipientQuery('');
                      }}
                    >
                      <Avatar name={recipient.name} avatarUrl={recipient.avatarUrl} size={22} />
                      <div>
                        <p className="text-xs font-semibold text-ink">{recipient.name}</p>
                        <p className="text-xs text-slate-500">{recipient.title || recipient.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <input
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder={tr('Subject')}
              value={newSubject}
              onChange={(event) => setNewSubject(event.target.value)}
            />

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tr('Patient tags')}</p>
              <div className="flex flex-wrap gap-2">
                {selectedPatients.map((patient) => (
                  <span key={patient.id} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs text-emerald-700">
                    <Avatar name={patient.name} avatarUrl={patient.avatarUrl} size={20} />
                    {patient.name}
                    <button type="button" onClick={() => setSelectedPatients((current) => current.filter((item) => item.id !== patient.id))}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="relative">
                <Tag size={14} className="absolute left-2 top-2.5 text-slate-400" />
                <input
                  className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm"
                  value={patientQuery}
                  onChange={(event) => setPatientQuery(event.target.value)}
                  placeholder={tr('Search patients')}
                />
              </div>
              {patientQuery && selectablePatients.length > 0 ? (
                <div className="max-h-28 overflow-auto rounded-lg border border-slate-200 bg-white">
                  {selectablePatients.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        setSelectedPatients((current) => [...current, patient]);
                        setPatientQuery('');
                      }}
                    >
                      <Avatar name={patient.name} avatarUrl={patient.avatarUrl} size={22} />
                      <p className="text-xs font-semibold text-ink">{patient.name}</p>
                      <p className="ml-auto text-[11px] text-slate-500">{patient.mrn}</p>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <RichEditor
                editorRef={newThreadEditorRef}
                value={newMessageHtml}
                onChange={(next) => {
                  setNewMessageHtml(next);
                  setNewMessage(htmlToPlainText(next));
                }}
                placeholder={tr('Write the first message...')}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs"
                  onClick={() => newThreadAttachmentInputRef.current?.click()}
                  disabled={uploadingNewThreadAttachment}
                >
                  <Paperclip size={13} />
                  {uploadingNewThreadAttachment ? tr('Uploading...') : tr('Attach')}
                </button>
                <input
                  ref={newThreadAttachmentInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => {
                    const files = Array.from(event.target.files || []);
                    if (files.length) void uploadMessageAttachments(files, setNewThreadAttachments, setUploadingNewThreadAttachment);
                    event.currentTarget.value = '';
                  }}
                />
                <select
                  className="rounded-md border border-slate-200 px-2 py-1 text-xs"
                  value={newThreadReferenceKind}
                  onChange={(event) => setNewThreadReferenceKind(event.target.value as LookupReference['kind'])}
                >
                  <option value="APPOINTMENT">Appointment</option>
                  <option value="LAB_REPORT">Lab report</option>
                  <option value="RADIOLOGY_STUDY">Radiology study</option>
                  <option value="RADIOLOGY_REPORT">Radiology report</option>
                </select>
                <input
                  className="rounded-md border border-slate-200 px-2 py-1 text-xs"
                  value={newThreadReferenceQuery}
                  onChange={(event) => setNewThreadReferenceQuery(event.target.value)}
                  placeholder={tr('Tag records')}
                />
              </div>
              {newThreadReferenceQuery && selectableNewThreadRefs.length > 0 ? (
                <div className="max-h-24 overflow-auto rounded-lg border border-slate-200 bg-white">
                  {selectableNewThreadRefs.map((item) => (
                    <button
                      key={`${item.kind}-${item.refId}`}
                      type="button"
                      className="w-full px-3 py-2 text-left text-xs hover:bg-slate-50"
                      onClick={() => {
                        setNewThreadEntityTags((current) => [...current, item]);
                        setNewThreadReferenceQuery('');
                      }}
                    >
                      <p className="font-semibold text-ink">{item.label}</p>
                      {item.subtitle ? <p className="text-slate-500">{item.subtitle}</p> : null}
                    </button>
                  ))}
                </div>
              ) : null}
              {(newThreadEntityTags.length || newThreadAttachments.length) ? (
                <div className="flex flex-wrap gap-2">
                  {newThreadEntityTags.map((tag) => (
                    <span key={`${tag.kind}-${tag.refId}`} className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] text-sky-700">
                      {tag.label}
                      <button type="button" onClick={() => setNewThreadEntityTags((current) => current.filter((item) => !(item.kind === tag.kind && item.refId === tag.refId)))}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                  {newThreadAttachments.map((file, idx) => (
                    <span key={`${file.fileName}-${idx}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600">
                      {file.fileName}
                      <button type="button" onClick={() => setNewThreadAttachments((current) => current.filter((_, i) => i !== idx))}>
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                onClick={closeCompose}
              >
                {tr('Close')}
              </button>
              <button
                type="button"
                className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                onClick={() => void createThread()}
                disabled={creatingThread || (!newMessage.trim() && !newThreadAttachments.length) || selectedRecipients.length === 0}
              >
                {creatingThread ? tr('Creating...') : tr('Send')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showCreateFolderDialog ? (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <p className="text-sm font-semibold text-ink">{tr('Create folder')}</p>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                onClick={() => {
                  setShowCreateFolderDialog(false);
                  setNewFolderName('');
                }}
                aria-label={tr('Close')}
              >
                <X size={14} />
              </button>
            </div>
            <div className="space-y-3 p-4">
              <input
                autoFocus
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder={tr('Folder name')}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700"
                  onClick={() => {
                    setShowCreateFolderDialog(false);
                    setNewFolderName('');
                  }}
                >
                  {tr('Cancel')}
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={() => void createFolder()}
                  disabled={creatingFolder || !newFolderName.trim()}
                >
                  {creatingFolder ? tr('Creating...') : tr('Create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {error ? <div className="border-t border-rose-200 bg-rose-50 px-4 py-2 text-xs text-rose-700">{error}</div> : null}
    </div>
  );
}
